import styled, { css } from 'styled-components';

import { CloseIcon } from 'components/ui/icons';
import { Clickable } from 'components/ui/shared/Buttons/Clickable';
import { ElementTestId } from 'enums/testing';
import { clearGraphBaseURL, getGraphURL } from 'utils/apiUtils';
import { t } from 'utils/intlUtils';
import { disableLogging, isLoggingEnabledLocally } from 'utils/loggingUtils';
import { isCypressCI, isLocallyServed } from 'utils/runtimeUtils';
import { hexToRGBA, negate } from 'utils/styledUtils';

import {
  NEUTRAL_0,
  SPACE_200,
  SPACE_500,
  SURFACE_DESTRUCTIVE_DESTRUCTIVE_BOLD,
  WIDTH_500,
  Z_INDEX_5,
} from 'styles/tokens';

const ProductionModeWarning = styled.div<{ $isClickable?: boolean }>`
  background: ${hexToRGBA(SURFACE_DESTRUCTIVE_DESTRUCTIVE_BOLD, '0.75')};
  bottom: 0;
  color: ${NEUTRAL_0};
  left: 50%;
  padding: ${SPACE_200} 10px;
  pointer-events: none;
  position: fixed;
  transform: translate(-50%, 0);
  z-index: ${Z_INDEX_5};

  ${({ $isClickable }) =>
    $isClickable &&
    css`
      cursor: pointer;
      pointer-events: auto;
    `};
`;

const ProductionClearButton = styled(Clickable)`
  background: ${hexToRGBA(SURFACE_DESTRUCTIVE_DESTRUCTIVE_BOLD, '0.75')};
  color: ${NEUTRAL_0};
  padding: ${SPACE_200} 0;
  pointer-events: auto;
  position: fixed;
  right: ${negate(SPACE_500)};
  top: 0;
  width: ${WIDTH_500};
  z-index: ${Z_INDEX_5};
`;

const WarningLabel = styled.span`
  pointer-events: none;
  user-select: none;
`;

/**
 * Which API this build talks to. Previews run against the dev services env, so they are
 * identified by their own telemetry env name instead.
 */
const getApiLabel = () => {
  if (process.env.REACT_APP_FEATURE_DATADOG_ENV_NAME === 'preview') {
    return 'PREVIEW';
  }

  switch (process.env.REACT_APP_SERVICES_ENV) {
    case 'dev': {
      return 'DEV';
    }

    case 'localhost': {
      return 'LOCAL API';
    }

    case 'prod': {
      return 'PROD';
    }

    case 'stage': {
      return 'STAGE';
    }

    default: {
      return 'Warning: Env N/A';
    }
  }
};

const getBranchSuffix = () => {
  const branch = process.env.REACT_APP_BUILD_BRANCH;

  return branch && process.env.REACT_APP_FEATURE_ENV_WARNING_BRANCH === 'true' ? ` · ${branch}` : '';
};

/*
 * Locally served sessions always name telemetry outright and say whether it is reporting.
 * Saying nothing used to read as "not reporting", which is the ambiguity that let local
 * traffic reach Datadog unnoticed — and a bare "logging off" read as a sign-out notice.
 * The suffix rendering at all is what marks the session local; it needn't say so, since a
 * loopback hostname is already in the address bar.
 */
const getLocalSuffix = () => {
  if (!isLocallyServed()) {
    return '';
  }

  if (isLoggingEnabledLocally()) {
    return ' · telemetry: ON — click to turn off';
  }

  /*
   * The CI exemption reports despite the storage gate saying otherwise, and its runs are
   * screenshotted — claiming telemetry is off there would be the same false reassurance.
   */
  return isCypressCI() ? ' · telemetry: on (Cypress CI)' : ' · telemetry: off';
};

const EnvironmentModeWarning = () => {
  const isDifferentEnv = getGraphURL() !== process.env.REACT_APP_GRAPH_BASE;
  const debugWarning = `${t('debug_mode')}: ${getGraphURL()}`;
  const warningLabel = `${isDifferentEnv ? debugWarning : getApiLabel()}${getBranchSuffix()}${getLocalSuffix()}`;

  const onDisableLoggingClicked = () => (isLoggingEnabledLocally() ? disableLogging() : undefined);

  /*
   * Every locally served session shows the banner, whatever its profile has env debugging set to:
   * the suffix is the only place a local session states its telemetry status, and the only way to
   * click an opt-in back off. The localhost profile in particular keeps env debugging off for
   * `getGraphURL`'s sake, and shares an origin — and therefore the opt-in storage key — with the
   * profile the opt-in was likely made on.
   */
  const isVisible = process.env.REACT_APP_FEATURE_ENV_DEBUGGING === 'true' || isLocallyServed();

  return isVisible ? (
    <div>
      <ProductionModeWarning $isClickable={isLoggingEnabledLocally()} onClick={onDisableLoggingClicked}>
        <WarningLabel data-testid={ElementTestId.ENVIRONMENT_MODE_WARNING}>{warningLabel}</WarningLabel>
        {isDifferentEnv ? (
          <ProductionClearButton onClick={() => clearGraphBaseURL()}>
            <CloseIcon />
          </ProductionClearButton>
        ) : null}
      </ProductionModeWarning>
    </div>
  ) : null;
};

export default EnvironmentModeWarning;
